/**
 * FinanceOverview — Finance dashboard summary: honest KPI stats, a pending-invoice
 * queue (overdue surfaced first with the attention treatment), a "needs attention"
 * panel, and an expense-by-category breakdown.
 *
 * Data is honest at the source: /v1/finance/stats returns deduped figures and
 * /v1/finance/invoices a deduped list (natural-key dedup, migration 0059) — so
 * there is NO client-side dedup here. Every card figure reads server stats
 * directly — count, overdue £ and oldest due date share one SQL basis since
 * SB-11 (the pending-only reducer that made the count and the sum disagree is
 * gone). Colours/sizes flow from theme/tokens.ts; overdue uses the attention.*
 * amber family (finance quiet-urgency).
 */
import { LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { usePolledAsync, fetchFinanceStats, fetchInvoices, fetchExpenses } from '../../api'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts'
import { attention, chartCategorical, ink, edge, shadow, surface } from '../../../../theme/tokens'
import type { FinanceInvoice } from '../../types'
import { CATEGORIES } from './FinanceExpenses'
import { withNonGbpNote } from '../../../../lib/money'

// Category → FIXED palette slot by its position in the canonical picklist
// vocabulary (the expense form's CATEGORIES — the only enforcement the field
// has). Binding by slot, not by whatever categories the data happens to hold,
// keeps a category's hue stable as data populates (the standing chart rule:
// bind BEFORE empty slices drop, or the palette reshuffles). A legacy value
// outside the vocabulary takes the final neutral slot.
function categoryFill(category: string): string {
  const idx = (CATEGORIES as readonly string[]).indexOf(category)
  return idx >= 0 ? chartCategorical[idx] : chartCategorical[chartCategorical.length - 1]
}

// Full honest GBP (KPIs, amounts, sums) — no abbreviation, no fabricated precision.
function fmtGBP(n: number): string {
  return `£${Math.round(n).toLocaleString('en-GB')}`
}
// Compact GBP for the chart axis only.
// Exported for the copies-agree test only (cardRuleCopies.test.ts); nothing else imports it.
export function moneyAxis(n: number): string {
  // Boundary (register row 67): a value that would ROUND to 1000 in the smaller
  // unit rolls to the next one — £999,500 and up prints in M (never "£1000k"),
  // £999.50 and up in k (never "£1000"). The same two thresholds in every copy.
  if (n >= 999_500) return `£${(n / 1_000_000).toFixed(1)}M`
  if (n >= 999.5) return `£${(n / 1_000).toFixed(0)}k`
  return `£${n.toFixed(0)}`
}
function fmtDate(d?: string): string {
  return d ? new Date(d).toLocaleDateString('en-GB') : '—'
}
function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

// Date-only overdue age, matching the server's `due_date < CURRENT_DATE`.
function daysOverdue(dueDate?: string): number {
  if (!dueDate) return 0
  const due = new Date(dueDate)
  due.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diff = Math.floor((today.getTime() - due.getTime()) / 86_400_000)
  return diff > 0 ? diff : 0
}
function isOverdue(inv: FinanceInvoice): boolean {
  if (inv.status === 'paid' || inv.status === 'rejected') return false
  return daysOverdue(inv.dueDate) > 0
}

/**
 * The k-metric widget for the Finance pages (S-Tier slice 4) — the
 * OpsMetric/SalesMetric precedent verbatim: markup-level reuse living WITH
 * the department; DashboardShared's MetricCard and its non-Finance consumers
 * stay untouched. The amber tone carries over from MetricCard unchanged —
 * finance's quiet-urgency attention treatment (bg + border only), meaningful
 * colour, never decoration.
 */
export function FinanceMetric({ label, value, note, tone = 'default' }: {
  label: string
  value: string | number
  note?: string
  tone?: 'default' | 'amber'
}) {
  const amber = tone === 'amber'
  return (
    <div className="k-metric"
      style={amber ? { background: attention['banner-bg'], borderColor: attention['banner-border'] } : undefined}>
      <div className="flex items-center justify-between gap-2">
        <span className="k-metric-label uppercase">{label}</span>
      </div>
      <div className="flex flex-col gap-[7px]">
        <div className="flex items-baseline gap-2">
          <span className="k-metric-value">{value}</span>
        </div>
        {note && <div className="k-metric-note">{note}</div>}
      </div>
    </div>
  )
}

export default function FinanceOverview({ onNavigate }: { onNavigate: (page: string) => void }) {
  const { data, loading, error } = usePolledAsync(
    () => Promise.all([fetchFinanceStats(), fetchInvoices(), fetchExpenses()]),
    10_000,
    [],
  )

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const [stats, invoices, expenses] = data!

  // Pending queue: overdue first, then soonest due. No dedup — deduped at source.
  const pending = invoices
    .filter(i => i.status === 'pending_review')
    .sort((a, b) => {
      const ao = isOverdue(a) ? 0 : 1
      const bo = isOverdue(b) ? 0 : 1
      if (ao !== bo) return ao - bo
      const ad = a.dueDate ? new Date(a.dueDate).getTime() : Infinity
      const bd = b.dueDate ? new Date(b.dueDate).getTime() : Infinity
      return ad - bd
    })

  // SB-11: count, £ sum and oldest age are ALL server figures on ONE basis
  // (status not paid/rejected AND past due — the whole-sum SQL Reports also
  // reads). Nothing on the card is derived from the pending queue any more;
  // the queue below is presentation only. The server sends the oldest DUE
  // DATE, not an age — today-relative arithmetic stays client-side.
  const overdueCount = stats.invoices.overdueCount
  const overdueValue = stats.invoices.overdueValue ?? 0
  const oldestDays = daysOverdue(stats.invoices.overdueOldestDueDate)

  // Expense breakdown by category (derived): EVERY expense the list call
  // returned, whatever its status — fetchExpenses sends no status filter, so
  // a pending expense is charted like an approved one. The card says so; do
  // not reintroduce an 'approved' claim without also filtering here.
  const byCategory = new Map<string, number>()
  for (const e of expenses) {
    const cat = e.category || 'other'
    byCategory.set(cat, (byCategory.get(cat) ?? 0) + (e.amount ?? 0))
  }
  const categoryData = [...byCategory.entries()].map(([category, value]) => ({ category, value }))

  const pendingReview = stats.invoices.pendingReview
  const approvedThisMonth = stats.invoices.approvedThisMonth
  const expensesPending = stats.expenses.pending

  return (
    <div className="page-enter flex flex-col gap-4 mx-auto" style={{ maxWidth: 1120, minWidth: 1040 }}>
      {/* KPI row — every figure (counts AND the overdue £) from server stats */}
      <div className="grid grid-cols-4 gap-3">
        <FinanceMetric
          label="Pending"
          value={fmtGBP(stats.invoices.pendingValue)}
          note={withNonGbpNote(
            pendingReview > 0
              ? `${pendingReview} ${plural(pendingReview, 'invoice', 'invoices')} awaiting approval`
              : 'Nothing awaiting approval',
            stats.invoices.pendingValueExcludedNonGbp, 'invoice')}
        />
        <FinanceMetric
          label="Approved this month"
          value={approvedThisMonth}
          note={approvedThisMonth > 0 ? 'Approved and pushed to Xero' : 'None approved yet this month'}
        />
        <FinanceMetric
          label="Overdue"
          value={overdueCount}
          tone={overdueCount > 0 ? 'amber' : 'default'}
          note={overdueCount > 0
            ? withNonGbpNote(`${fmtGBP(overdueValue)} past due`, stats.invoices.overdueExcludedNonGbp, 'invoice')
            : 'All invoices on time'}
        />
        <FinanceMetric
          label="Expenses pending"
          value={expensesPending}
          note={withNonGbpNote(
            expensesPending > 0
              ? `${fmtGBP(stats.expenses.pendingValue)} awaiting approval`
              : 'No expenses awaiting approval',
            stats.expenses.pendingValueExcludedNonGbp, 'expense')}
        />
      </div>

      <div className="grid grid-cols-[1fr_320px] gap-4">
        {/* Pending invoices — overdue surfaced first with the attention treatment */}
        <div className="k-card">
          <div className="k-card-head">
            <div>
              <div className="k-card-title">Pending invoices</div>
              <div className="k-card-sub">Overdue first, then by due date</div>
            </div>
            <button className="k-card-link cursor-pointer bg-transparent border-0 p-0 uppercase hover:underline"
              onClick={() => onNavigate('finance.invoices')}>See all</button>
          </div>
          {pending.length === 0 ? (
            <EmptyState message="Nothing awaiting approval" hint="Approved invoices move on to Xero" />
          ) : (
            <table className="closr-table finance-table w-full">
              <thead>
                <tr>
                  <th className="text-left">Vendor</th>
                  <th className="text-right">Amount</th>
                  <th className="text-left">Due</th>
                </tr>
              </thead>
              <tbody>
                {pending.slice(0, 8).map(inv => {
                  const od = isOverdue(inv)
                  const d = daysOverdue(inv.dueDate)
                  return (
                    <tr
                      key={inv.id}
                      className="cursor-pointer"
                      onClick={() => onNavigate('finance.invoices')}
                      style={od ? { borderLeft: `2px solid ${attention.indicator}`, background: attention['row-tint'] } : undefined}
                    >
                      <td>
                        <div className="font-medium text-ink-primary">{inv.vendorName}</div>
                        <div className="text-11 text-ink-muted">{inv.invoiceNumber || '—'}</div>
                        {od && (
                          <div className="flex items-center gap-1 mt-1">
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: attention.indicator }} aria-hidden="true" />
                            <span className="text-10 font-semibold" style={{ color: attention.wordmark, letterSpacing: '0.04em' }}>
                              OVERDUE · {d} {plural(d, 'DAY', 'DAYS')}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="text-right font-semibold text-ink-primary tabular-nums">{fmtGBP(inv.amount ?? 0)}</td>
                      <td className="text-12 text-ink-secondary">{fmtDate(inv.dueDate)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Sidebar: attention panel + expenses by category */}
        <div className="flex flex-col gap-4">
          <div className="k-card">
            <div className="k-card-head">
              <div className="k-card-title">Needs attention</div>
            </div>
            <div className="p-4">
              {overdueCount > 0 ? (
                <button
                  onClick={() => onNavigate('finance.invoices')}
                  className="w-full text-left rounded-tile p-3"
                  style={{ background: attention['banner-bg'], border: `1px solid ${attention['banner-border']}` }}
                >
                  <div className="flex items-center gap-2">
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: attention.indicator }} aria-hidden="true" />
                    <span className="text-13 font-semibold" style={{ color: attention['banner-text'] }}>
                      {overdueCount} {plural(overdueCount, 'invoice', 'invoices')} overdue
                    </span>
                  </div>
                  <div className="text-12 mt-1" style={{ color: attention['banner-text'] }}>
                    {withNonGbpNote(`${fmtGBP(overdueValue)} past due`, stats.invoices.overdueExcludedNonGbp, 'invoice')}
                    {oldestDays > 0 && ` · oldest ${oldestDays} ${plural(oldestDays, 'day', 'days')}`}
                  </div>
                </button>
              ) : (
                <EmptyState message="Nothing overdue" hint="Overdue invoices will surface here" />
              )}
            </div>
          </div>

          <div className="k-card">
            <div className="k-card-head">
              <div className="k-card-title">Expenses by category</div>
            </div>
            <div className="p-4">
              {categoryData.length === 0 ? (
                <EmptyState message="No expenses yet" hint="Expenses appear here grouped by category, whatever their approval status" />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={categoryData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={edge['row-faint']} horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 9, fill: ink.faint }} axisLine={false} tickLine={false} tickFormatter={v => moneyAxis(v)} />
                    <YAxis type="category" dataKey="category" tick={{ fontSize: 10, fill: ink.faint }} axisLine={false} tickLine={false} width={70} />
                    <Tooltip contentStyle={{ background: surface.card, border: 'none', borderRadius: 10, fontSize: 11, boxShadow: shadow.tooltip }} formatter={(v: number) => fmtGBP(v)} cursor={{ fill: surface['row-hover'] }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {categoryData.map(d => <Cell key={d.category} fill={categoryFill(d.category)} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
